import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type SelectOption,
} from '../../ui';
import { MultiPicker } from '../common/MultiPicker';
import {
  DEFAULT_DOC_TYPE,
  DOCUMENT_TYPES,
} from '../../features/documents/documentConfig';
import {
  blankDocumentDraft,
  draftFromDocument,
  listToInput,
  parseList,
  validateDocumentDraft,
} from '../../features/documents/documentModel';
import type {
  StructuredDocumentFields,
} from '../../features/documents/aiStructure';
import type {
  TeamDocument,
  TeamDocumentDraft,
} from '../../features/documents/types';
import type { GoalSummary } from '../../features/goals/types';
import type { WorkstreamSummary } from '../../features/workstreams/types';
import type { Task } from '../../features/tasks/types';
import type { ServiceSummary } from '../../features/services/types';
import type { DecisionSummary } from '../../features/decisions/types';
import type { TeamDocumentSummary } from '../../features/documents/types';

export interface DocumentModalProps {
  open: boolean;
  onClose: () => void;
  /** Default owner alias (the current user). */
  defaultOwner: string;
  /** When editing, the hydrated document; omit to register a new one. */
  initialDoc?: TeamDocument;
  goals: GoalSummary[];
  workstreams: WorkstreamSummary[];
  tasks: Task[];
  services: ServiceSummary[];
  decisions: DecisionSummary[];
  documents: TeamDocumentSummary[];
  serviceTitles: Map<string, string>;
  storyTitles: Map<string, string>;
  decisionTitles: Map<string, string>;
  onSubmit: (draft: TeamDocumentDraft) => Promise<unknown>;
  /** AI-STRUCTURE: structure free-form notes into partial draft fields. */
  structureFromNotes: (notes: string) => Promise<StructuredDocumentFields>;
}

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'IN_REVIEW', label: 'In review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DEPRECATED', label: 'Deprecated' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const DOC_TYPE_OPTIONS: SelectOption[] = DOCUMENT_TYPES.map((t) => ({
  value: t.value,
  label: t.label,
}));

/**
 * Register / edit a TeamDocument (a link + metadata, never the content). The AI
 * panel STRUCTURES pasted notes into a suggested draft (the AI-STRUCTURE step)
 * which the human then VERIFIES/edits before saving — nothing is trusted
 * blindly. Manual entry is always available; the AI panel is optional.
 */
export function DocumentModal({
  open,
  onClose,
  defaultOwner,
  initialDoc,
  goals,
  workstreams,
  tasks,
  services,
  decisions,
  documents,
  serviceTitles,
  storyTitles,
  decisionTitles,
  onSubmit,
  structureFromNotes,
}: DocumentModalProps) {
  const [draft, setDraft] = useState<TeamDocumentDraft>(blankDocumentDraft());
  const [authorsText, setAuthorsText] = useState('');
  const [reviewersText, setReviewersText] = useState('');
  const [reviewedWithText, setReviewedWithText] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [notes, setNotes] = useState('');
  const [structuring, setStructuring] = useState(false);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = initialDoc
      ? draftFromDocument(initialDoc)
      : blankDocumentDraft({ ownerPoc: defaultOwner });
    setDraft(base);
    setAuthorsText(listToInput(base.authors));
    setReviewersText(listToInput(base.reviewers));
    setReviewedWithText(listToInput(base.reviewedWith));
    setTagsText(listToInput(base.tags));
    setNotes('');
    setAiNote(null);
    setProblems([]);
  }, [open, initialDoc, defaultOwner]);

  const set = <K extends keyof TeamDocumentDraft>(key: K, value: TeamDocumentDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const goalOptions: SelectOption[] = goals.map((g) => ({ value: g.id, label: g.title || g.id }));
  const workstreamOptions: SelectOption[] = workstreams.map((w) => ({
    value: w.id,
    label: w.title || w.id,
  }));
  const serviceOptions: SelectOption[] = services.map((s) => ({ value: s.id, label: s.name || s.id }));
  const storyOptions: SelectOption[] = tasks.map((t) => ({ value: t.id, label: t.title || t.id }));
  const decisionOptions: SelectOption[] = decisions.map((d) => ({
    value: d.id,
    label: d.decision || d.id,
  }));
  const supersedeOptions: SelectOption[] = documents
    .filter((d) => d.id !== initialDoc?.id)
    .map((d) => ({ value: d.id, label: d.title || d.id }));

  const buildDraft = (): TeamDocumentDraft => ({
    ...draft,
    title: draft.title.trim(),
    summary: draft.summary.trim(),
    contextWhy: draft.contextWhy.trim(),
    documentLink: draft.documentLink.trim(),
    ownerPoc: draft.ownerPoc.trim(),
    authors: parseList(authorsText),
    reviewers: parseList(reviewersText),
    reviewedWith: parseList(reviewedWithText),
    tags: parseList(tagsText),
  });

  const runStructure = async () => {
    if (!notes.trim() || structuring) return;
    setStructuring(true);
    setAiNote(null);
    try {
      const fields = await structureFromNotes(notes);
      const applied: string[] = [];
      setDraft((d) => {
        const next = { ...d };
        if (fields.title) {
          next.title = fields.title;
          applied.push('title');
        }
        if (fields.docType) {
          next.docType = fields.docType;
          applied.push('type');
        }
        if (fields.summary) {
          next.summary = fields.summary;
          applied.push('summary');
        }
        if (fields.contextWhy) {
          next.contextWhy = fields.contextWhy;
          applied.push('context');
        }
        if (fields.tags) {
          next.tags = fields.tags;
          applied.push('tags');
        }
        if (fields.relatedServices) {
          // Keep only known service ids; free-text names are dropped (the human
          // links services explicitly below).
          const ids = new Set(services.map((s) => s.id));
          const matched = fields.relatedServices.filter((r) => ids.has(r));
          if (matched.length) {
            next.relatedServices = matched;
            applied.push('services');
          }
        }
        return next;
      });
      if (fields.tags) setTagsText(listToInput(fields.tags));
      setAiNote(
        applied.length
          ? `AI suggested: ${applied.join(', ')}. Review and edit before saving.`
          : 'AI could not extract structured fields from those notes.',
      );
    } catch (e) {
      setAiNote(e instanceof Error ? e.message : 'AI structuring failed.');
    } finally {
      setStructuring(false);
    }
  };

  const submit = async () => {
    const next = buildDraft();
    const found = validateDocumentDraft(next);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(next);
      onClose();
    } catch (e) {
      setProblems([e instanceof Error ? e.message : 'Failed to save document.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      size="xl"
      title={initialDoc ? 'Edit document' : 'Register document'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={submit}>
            {initialDoc ? 'Save changes' : 'Register document'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {problems.length > 0 && (
          <ul className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        )}

        {/* AI-STRUCTURE panel (optional). */}
        <div className="rounded-xl border border-surface-border bg-surface-1 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-zinc-200">
            <Sparkles className="h-4 w-4 text-accent" aria-hidden="true" />
            Structure from notes (optional)
          </div>
          <Textarea
            placeholder="Paste an abstract or free-form notes about the doc; AI proposes title, type, summary, context and tags for you to verify."
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="mt-2 flex items-center gap-3">
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<Sparkles className="h-3.5 w-3.5" />}
              loading={structuring}
              disabled={!notes.trim()}
              onClick={runStructure}
            >
              Structure with AI
            </Button>
            {aiNote && <span className="text-xs text-zinc-400">{aiNote}</span>}
          </div>
        </div>

        <Input
          label="Title (required)"
          value={draft.title}
          onChange={(e) => set('title', e.target.value)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Type"
            options={DOC_TYPE_OPTIONS}
            value={draft.docType || DEFAULT_DOC_TYPE}
            onChange={(e) => set('docType', e.target.value)}
          />
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            value={draft.status}
            onChange={(e) => set('status', e.target.value as TeamDocumentDraft['status'])}
          />
        </div>

        <Textarea
          label="Summary (required)"
          hint="Powers discovery + search."
          rows={2}
          value={draft.summary}
          onChange={(e) => set('summary', e.target.value)}
        />
        <Textarea
          label="Context — why it exists"
          rows={2}
          value={draft.contextWhy}
          onChange={(e) => set('contextWhy', e.target.value)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Document link (required)"
            placeholder="Quip / Wiki / etc. URL"
            value={draft.documentLink}
            onChange={(e) => set('documentLink', e.target.value)}
          />
          <Input
            label="Owner POC"
            placeholder="Who to ask about it"
            value={draft.ownerPoc}
            onChange={(e) => set('ownerPoc', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            type="date"
            label="Created date"
            value={draft.createdDate}
            onChange={(e) => set('createdDate', e.target.value)}
          />
          <Input
            type="date"
            label="Last reviewed"
            value={draft.lastReviewedDate ?? ''}
            onChange={(e) => set('lastReviewedDate', e.target.value || undefined)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Authors"
            hint="Comma-separated aliases"
            value={authorsText}
            onChange={(e) => setAuthorsText(e.target.value)}
          />
          <Input
            label="Reviewers"
            hint="Comma-separated aliases"
            value={reviewersText}
            onChange={(e) => setReviewersText(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Reviewed with"
            hint="Teams / groups, comma-separated"
            value={reviewedWithText}
            onChange={(e) => setReviewedWithText(e.target.value)}
          />
          <Input
            label="Tags"
            hint="Comma-separated"
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Related goal"
            placeholder="None"
            value={draft.relatedGoalId ?? ''}
            options={goalOptions}
            onChange={(e) => set('relatedGoalId', e.target.value || undefined)}
          />
          <Select
            label="Related workstream"
            placeholder="None"
            value={draft.relatedWorkstreamId ?? ''}
            options={workstreamOptions}
            onChange={(e) => set('relatedWorkstreamId', e.target.value || undefined)}
          />
        </div>

        <MultiPicker
          label="Related services"
          options={serviceOptions}
          value={draft.relatedServices}
          onChange={(ids) => set('relatedServices', ids)}
          labelFor={(id) => serviceTitles.get(id) ?? id}
        />
        <MultiPicker
          label="Related stories"
          options={storyOptions}
          value={draft.relatedStoryIds}
          onChange={(ids) => set('relatedStoryIds', ids)}
          labelFor={(id) => storyTitles.get(id) ?? id}
        />
        <MultiPicker
          label="Related decisions"
          options={decisionOptions}
          value={draft.relatedDecisionIds}
          onChange={(ids) => set('relatedDecisionIds', ids)}
          labelFor={(id) => decisionTitles.get(id) ?? id}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Related repo"
            placeholder="Optional package/repo"
            value={draft.relatedRepo ?? ''}
            onChange={(e) => set('relatedRepo', e.target.value || undefined)}
          />
          <Select
            label="Supersedes"
            placeholder="None"
            value={draft.supersedesId ?? ''}
            options={supersedeOptions}
            onChange={(e) => set('supersedesId', e.target.value || undefined)}
          />
        </div>
      </div>
    </Modal>
  );
}

export default DocumentModal;
